import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../Utils/axiosInstance";
import {
  getToken,
  getUser,
  logout as clearAuth,
  isAuthenticated as checkAuth,
} from "../../Utils/auth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const storedToken = getToken();
      const storedUser = getUser();

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(storedUser);
        await fetchProfile();
      }
      setLoading(false);
    };

    init();
  }, []);

  const setAuthData = (token, user) => {
    localStorage.setItem("qqc_userToken", token);
    localStorage.setItem("qqc_userData", JSON.stringify(user));
    setToken(token);
    setUser(user);
    window.dispatchEvent(
      new CustomEvent("auth:changed", { detail: { token } }),
    );
  };

  const register = async (payload) => {
    try {
      const res = await axiosInstance.post(`/user/register`, payload);
      toast.success(res.data.message || "Registered successfully");

      if (res.data.data?.token && res.data.data?.user) {
        localStorage.setItem("qqc_userToken", res.data.data.token);
        localStorage.setItem(
          "qqc_userData",
          JSON.stringify(res.data.data.user),
        );
        setToken(res.data.data.token);
        setUser(res.data.data.user);
      }

      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed");
      throw err;
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axiosInstance.get(`/user/get-profile`);
      const updatedUser = res.data.data.user;
      localStorage.setItem("qqc_userData", JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (err) {
      console.error("[AuthContext] Profile fetch failed:", err.message);
    }
  };

  const logout = () => {
    clearAuth();
    setUser(null);
    setToken(null);
    toast.success("Logged out successfully");
    window.dispatchEvent(
      new CustomEvent("auth:changed", { detail: { token: null } }),
    );
  };

  const updateUser = (updaterOrValue) => {
    setUser((prev) => {
      const next =
        typeof updaterOrValue === "function"
          ? updaterOrValue(prev)
          : updaterOrValue;
      localStorage.setItem("qqc_userData", JSON.stringify(next));
      return next;
    });
  };

  const value = {
    user,
    token,
    register,
    logout,
    setAuthData,
    updateUser,
    fetchProfile,
    isAuthenticated: !!token,
    isLoading: loading,
  };

  if (loading) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
